
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { formRules, regex} from "@/utils/formRules";
import { getSrc, removeVuetifyFileInputBug } from "@/utils/misc";
import { Getter } from "vuex-class";
import Telephone from "@/components/Telephone.vue";
import CustomBtn from "@/components/CustomBtn.vue";
import { defaultUserAvatar } from "@/utils/constants";
import RightsForm from "@/components/rights/RightsForm.vue";
import { canEditUserRights } from "@/services/rights";
import { userCompaniesIds } from "@/services/users";

@Component({
    components: {
        CustomBtn,
        Telephone,
        RightsForm,
    },
})
export default class EditUser extends Vue {

    get canEditCurrentUserRights(): boolean {
      const targetRole = this.user.ManagersRelations ? "MANAGERS" : "GUESTS";
      return canEditUserRights(this.currentUser, targetRole, this.user.id);
    }

    get isSignalActive(): boolean {
        return false;
    }
    @Prop({default: {}})
    public user: any;

    @Prop({default: null})
    public companyId: number;

    @Prop({default: false})
    public isMobile: boolean;

    @Prop({default: false})
    public editContact: boolean;

    public $refs!: {
      rightsForm: any,
    };

    @Getter private currentUser: any;
    @Getter private isAdmin: boolean;
    @Getter private envCompanyId: number|null;

    private rights: any[] = [];
    private userRights: any[] = [];
    private defaultUserAvatar: string = defaultUserAvatar;

    private getSrc: (picturePath: string) => string = getSrc;
    private removeVuetifyFileInputBug: (document: any) => void = removeVuetifyFileInputBug;
    private editUserDialog: boolean = false;
    private valid: boolean = true;
    private avatar: any = null;
    private uploadedImageSrc: any = null;
    private uploadFile: boolean = false;
    private changeAvatar: boolean = false;
    private deleteFile: boolean = false;
    private disablePhoneEdition: boolean = false;
    private disableEmailEdition: boolean = false;
    private disableNameEdition: boolean = false;
    private rules: object = formRules;
    private editRights: boolean = false;
    private selectedCompanyId: number = null;

    @Watch("user", {deep: true})
    public handlerUser(newVal, oldVal): void {
        if (!this.isAdmin && oldVal) {
            if (formRules.isEmail(newVal.email)) {
                this.disablePhoneEdition = false;
                this.disableEmailEdition = true;
            } else {
                this.disablePhoneEdition = true;
                this.disableEmailEdition = false;
            }
        }
        if (this.user.Contact && !this.user.Contact.email) {
            this.user.Contact.email = newVal.email;
        }
        if (this.user.cguAcceptedAt) {
            this.disableNameEdition = true;
        }
    }

    private beforeUpdate(): void {
        this.removeVuetifyFileInputBug(document);
    }

    private openDialog(): void {
      this.reset();
      this.editUserDialog = true;
    }

    private reset(): void {
        this.avatar = null;
        if (this.avatar && this.uploadFile) {
            this.user.picturePath = this.uploadedImageSrc;
        }
        this.avatar = null;
        this.deleteFile = false;
        this.uploadFile = false;
        this.editUserDialog = false;
        this.editRights = false;
        this.getRights();
        this.getUserRights();
    }

    private fileChange(): void {
        if (this.avatar) {
            const fr = new FileReader();
            fr.readAsDataURL(this.avatar);
            fr.addEventListener("load", () => {
                this.uploadedImageSrc = fr.result;
            });
            this.uploadFile = true;
            this.deleteFile = false;
        } else {
            this.uploadedImageSrc = null;
        }
    }

    private save(): void {
        let filePromise: Promise<any> = Promise.resolve();
        if (this.uploadFile) {
            this.user.picturePath = true;
            const formData = new FormData();
            formData.append("file", this.avatar);
            formData.append("id", this.user.id);
            formData.append("fileType", "profile-picture");
            formData.append("fileName", this.avatar.name);
            filePromise = this.$store.dispatch("files/upload", {formData});
        } else if (this.deleteFile) {
            this.user.picturePath = false;
            filePromise = this.$store.dispatch("files/delete", {
                userId: this.user.id,
                fileType: "profile-picture",
            });
        } else {
            delete this.user.picturePath;
        }
        Promise.all([
            filePromise,
            this.$store.dispatch("users/updateUser", {
                id: this.$route.params.id,
                user: this.user,
            }),
        ]).then((res) => {
            this.$emit("user-updated");
            this.reset();
            this.$store.commit("alerts/displaySuccess", {
                msg: this.$i18n?.t("actions.saved"),
            });
        }).catch((err) => {
            this.$store.commit("alerts/displayError", {
                msg: err,
            });
        }).finally(() => this.$wait.end("users/edit-user"));
    }

    private editContactInfo(): void {
        // to keep unicity
        this.$store.dispatch("companies/updateContact", {
            companyId: this.user.Contact.companyId,
            contactId: this.user.Contact.id,
            contact: {
                ...this.user.Contact,
              // allowSignal: this.user.allowSignal,
            },
        }).then((res) => {
            this.reset();
            this.$emit("reset");
            this.$store.commit("alerts/displaySuccess", {
                msg: this.$i18n?.t("actions.saved"),
            });
        }).catch((err) => {
            this.$store.commit("alerts/displayError", {
                msg: err,
            });
        }).finally(() => this.$wait.end("users/edit-user"));
    }

    private deleteUser(): void {
        if (confirm(this.$t("user.deleteConfirmation").toString())) {
            this.$store.dispatch("admin/users/deleteUserById", {
              id: this.user.id,
            }).then((res) => {
                this.reset();
            }).catch((error) => {
                this.$store.commit("alerts/displayHttpError", { error });
            }).finally(() => {
                this.$wait.end("users/delete-user");
            });
        }
    }

    private deleteAvatar(): void {
        this.avatar = null;
        this.uploadedImageSrc = null;
        this.user.picturePath = false;
        this.deleteFile = true;
        this.uploadFile = false;
    }

    private validEmail(email): boolean {
        return formRules.isEmail(email);
    }

    private validPhoneNumber(phone): boolean {
        return regex.validPhoneNumber.test(phone);
    }

    private closeEdit(): void {
        this.reset();
    }

    private saveRights(): Promise<any> {
        const rightsToSave = this.$refs.rightsForm.currentRights
          .filter((r) => r.isChecked).map((r) => r.id);
        return this.$store.dispatch("rights/saveUserRights", {
            userId: this.user.id,
            companyId: this.envCompanyId,
            rights: rightsToSave,
        }).then((res) => {
            this.$store.commit("alerts/displaySuccess", {
                msg: this.$i18n?.t("actions.saved"),
            });
            if (this.currentUser.id === this.user.id) {
              // Refresh current user rights
              this.$store.dispatch("oauth/userByCurrentToken");
            }
            this.closeEdit();
        }).catch((err) => {
            this.$store.commit("alerts/displayError", {
                msg: err?.response?.data?.error || err,
            });
        }).finally(() => this.$wait.end("users/save-rights"));
    }

    private async getRights(): Promise<any> {
      this.rights = await this.$store.dispatch("rights/getAllRights")
        .then((res) => res.rights);
    }

    private async getUserRights(): Promise<void> {
      this.userRights = await this.$store.dispatch("rights/getUserRightsById", {
          userId: this.user.id,
          companyId: this.envCompanyId,
        }).then((userRights) => userRights.rights);
    }

    private selectCompany({ selectedCompany }): void {
      this.selectedCompanyId = selectedCompany.id;
    }
}
